import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { usePdf } from '../../../../hooks/usePdf';
import { fileNameEdit } from 'helpers/FileUtils';


const api = new AnalyticsApi('charts/kpi/');
const format = (v) => `${v}%`;

const useKpi = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    table1: {
      columns: [
        {
          title: t('Periodo'),
          dataIndex: 'Periodo',
        },
        {
          title: t('Footfall YTD'),
          dataIndex: 'Footfall YTD',
          render: (v) => Helpers.numberWithCommas(v),
        },
        {
          title: t('Sales YTD'),
          dataIndex: 'Sales YTD',
          render: (v) => Helpers.numberWithCommas(v, ' €'),
        },
        {
          title: t('CR YTD'),
          dataIndex: 'CR YTD',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
        {
          title: t('ATV YTD'),
          dataIndex: 'ATV YTD',
          render: (v) => Helpers.numberWithCommas(v, '€'),
        },
        {
          title: t('UPT YTD'),
          dataIndex: 'UPT YTD',
          render: (v) => Helpers.numberWithCommas(v),
        },
        {
          title: t('Tickets YTD'),
          dataIndex: 'Tickets YTD',
          render: (v) => Helpers.numberWithCommas(v),
        },
        {
          title: t('Pezzi YTD'),
          dataIndex: 'Pezzi YTD',
          render: (v) => Helpers.numberWithCommas(v),
        },
      ],
      data: [],
      total: {},
    },
    table2: {
      columns: [
        {
          title: t('Periodo'),
          dataIndex: 'Periodo',
        },
        {
          title: t('Footfall Vs LY'),
          dataIndex: 'Footfall Vs LY',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
        {
          title: t('Sales Vs LY'),
          dataIndex: 'Sales Vs LY',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
        {
          title: t('CR Vs LY'),
          dataIndex: 'CR Vs LY',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
        {
          title: t('ATV Vs LY'),
          dataIndex: 'ATV Vs LY',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
        {
          title: t('UPT Vs LY'),
          dataIndex: 'UPT Vs LY',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
        {
          title: t('Tickets Vs LY'),
          dataIndex: 'Tickets Vs LY',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
        {
          title: t('Pezzi Vs LY'),
          dataIndex: 'Pezzi Vs LY',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
      ],
      data: [],
      total: {},
    },
    table3: {
      columns: [
        {
          title: t('Periodo'),
          dataIndex: 'Periodo',
        },
        {
          title: t('Footfall Vs LY'),
          dataIndex: 'Footfall Vs LY',
          render: (v) => Helpers.numberWithCommas(v),
        },
        {
          title: t('Sales Vs LY'),
          dataIndex: 'Sales Vs LY',
          render: (v) => Helpers.numberWithCommas(v, ' €'),
        },
        {
          title: t('CR Vs LY'),
          dataIndex: 'CR Vs LY',
          render: (v) => Helpers.changeDotToComa(format(v)),
        },
        {
          title: t('ATV Vs LY'),
          dataIndex: 'ATV Vs LY',
          render: (v) => Helpers.numberWithCommas(v, '€'),
        },
        {
          title: t('UPT Vs LY'),
          dataIndex: 'UPT Vs LY',
          render: (v) => Helpers.numberWithCommas(v),
        },
        {
          title: t('Tickets Vs LY'),
          dataIndex: 'Tickets Vs LY',
          render: (v) => Helpers.numberWithCommas(v),
        },
        {
          title: t('Pezzi Vs LY'),
          dataIndex: 'Pezzi Vs LY',
          render: (v) => Helpers.numberWithCommas(v),
        },
      ],
      data: [],
      total: {},
    },
  });

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const summary = useCallback(() => {
    const { total = {}, columns = [] } = data.table1;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  const summary2 = useCallback(() => {
    const { total = {}, columns = [] } = data.table2;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  const summary3 = useCallback(
    (pageData) => {
      const { total = {}, columns = [] } = data.table3;

      delete total.Periodo;

      const tableKeys = Object.keys(total);
      const filteredColumns = columns.filter((column) =>
        tableKeys.includes(column.dataIndex),
      );

      return (
        <Table.Summary fixed>
          <Table.Summary.Row>
            <Table.Summary.Cell>
              <Text strong>{t('Totale')}</Text>
            </Table.Summary.Cell>
            {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
              <Table.Summary.Cell kry={dataIndex}>
                <Text strong>{render(total[dataIndex])}</Text>
              </Table.Summary.Cell>
            ))}
          </Table.Summary.Row>
        </Table.Summary>
      );
    },
    [data],
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let table1, table2, table3;

        if (!isAllReports) {
          const {
            data: {
              table1: backTable1,
              table2: backTable2,
              table3: backTable3,
            } = {},
          } = await api.getFootfalls({ params });
          table1 = backTable1;
          table2 = backTable2;
          table3 = backTable3;
        } else {
          const {
            table1: backTable1,
            table2: backTable2,
            table3: backTable3,
          } = allReportsData;
          table1 = backTable1;
          table2 = backTable2;
          table3 = backTable3;
        }

        if (!table1 || !table2 || !table3) return;

        const tableKeys1 = Object.keys(table1.total || {});
        const tableKeys2 = Object.keys(table2.total || {});
        const tableKeys3 = Object.keys(table3.total || {});

        const filteredColumns1 = data.table1.columns.filter((column) =>
          tableKeys1.includes(column.dataIndex),
        );
        const filteredColumns2 = data.table2.columns.filter((column) =>
          tableKeys2.includes(column.dataIndex),
        );
        const filteredColumns3 = data.table3.columns.filter((column) =>
          tableKeys3.includes(column.dataIndex),
        );

        setData((s) => ({
          table1: {
            ...s.table1,
            ...table1,
            columns: filteredColumns1,
          },
          table2: {
            ...s.table2,
            ...table2,
            columns: filteredColumns2,
          },
          table3: {
            ...s.table3,
            ...table3,
            columns: filteredColumns3,
          },
        }));
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData({
          table1: {
            columns: [
              {
                title: t('Periodo'),
                dataIndex: 'Periodo',
              },
              {
                title: t('Footfall YTD'),
                dataIndex: 'Footfall YTD',
                render: (v) => Helpers.numberWithCommas(v),
              },
              {
                title: t('Sales YTD'),
                dataIndex: 'Sales YTD',
                render: (v) => Helpers.numberWithCommas(v),
              },
              {
                title: t('CR YTD'),
                dataIndex: 'CR YTD',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
              {
                title: t('ATV YTD'),
                dataIndex: 'ATV YTD',
                render: (v) => Helpers.numberWithCommas(v, '€'),
              },
              {
                title: t('UPT YTD'),
                dataIndex: 'UPT YTD',
                render: (v) => Helpers.numberWithCommas(v),
              },
              {
                title: t('Tickets YTD'),
                dataIndex: 'Tickets YTD',
                render: (v) => Helpers.numberWithCommas(v),
              },
              {
                title: t('Pezzi YTD'),
                dataIndex: 'Pezzi YTD',
                render: (v) => Helpers.numberWithCommas(v),
              },
            ],
            data: [],
            total: {},
          },
          table2: {
            columns: [
              {
                title: t('Periodo'),
                dataIndex: 'Periodo',
              },
              {
                title: t('Footfall Vs LY'),
                dataIndex: 'Footfall Vs LY',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
              {
                title: t('Sales Vs LY'),
                dataIndex: 'Sales Vs LY',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
              {
                title: t('CR Vs LY'),
                dataIndex: 'CR Vs LY',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
              {
                title: t('ATV Vs LY'),
                dataIndex: 'ATV Vs LY',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
              {
                title: t('UPT Vs LY'),
                dataIndex: 'UPT Vs LY',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
              {
                title: t('Tickets Vs LY'),
                dataIndex: 'Tickets Vs LY',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
              {
                title: t('Pezzi Vs LY'),
                dataIndex: 'Pezzi Vs LY',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
            ],
            data: [],
            total: {},
          },
          table3: {
            columns: [
              {
                title: t('Periodo'),
                dataIndex: 'Periodo',
              },
              {
                title: t('Footfall Vs LY'),
                dataIndex: 'Footfall Vs LY',
                render: (v) => Helpers.numberWithCommas(v),
              },
              {
                title: t('Sales Vs LY'),
                dataIndex: 'Sales Vs LY',
                render: (v) => Helpers.numberWithCommas(v),
              },
              {
                title: t('CR Vs LY'),
                dataIndex: 'CR Vs LY',
                render: (v) => Helpers.changeDotToComa(format(v)),
              },
              {
                title: t('ATV Vs LY'),
                dataIndex: 'ATV Vs LY',
                render: (v) => Helpers.numberWithCommas(v, '€'),
              },
              {
                title: t('UPT Vs LY'),
                dataIndex: 'UPT Vs LY',
                render: (v) => Helpers.numberWithCommas(v),
              },
              {
                title: t('Tickets Vs LY'),
                dataIndex: 'Tickets Vs LY',
                render: (v) => Helpers.numberWithCommas(v),
              },
              {
                title: t('Pezzi Vs LY'),
                dataIndex: 'Pezzi Vs LY',
                render: (v) => Helpers.numberWithCommas(v),
              },
            ],
            data: [],
            total: {},
          },
        });
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          const event = new CustomEvent('status', { detail: 'ok' });

          setTimeout(() => {
            window.dispatchEvent(event);
          }, 5000);
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    data,
    filters,
    summary,
    summary2,
    summary3,
    format,
    title,
    loading,
    handleDownloadExcel,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useKpi;
